import React, { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router';

/**
 * SharedReportRedirect Component
 * v10.0.2 - 2026-03-11 15:00 - Deep linking support
 * 
 * Redirects shared report links to the main app with the report details.
 * This ensures users see the report in its natural context within the app feed.
 * 
 * Flow:
 * 1. User clicks shared link: /share/report?id=123&type=traffic&...
 * 2. This component captures the parameters
 * 3. Redirects to: /app?sharedReport=123&reportType=traffic&...
 * 4. MainApp detects the parameters and navigates to the correct tab
 * 5. The report is highlighted in the feed
 */
export function SharedReportRedirect() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    console.log('🔄 SharedReportRedirect: Redirecting to main app...');
    
    // Get all report parameters
    const reportId = searchParams.get('id');
    const reportType = searchParams.get('type');
    const location = searchParams.get('location');
    const description = searchParams.get('description');
    const reporter = searchParams.get('reporter');
    const timestamp = searchParams.get('timestamp');

    console.log('📊 Report params:', {
      reportId,
      reportType,
      location,
      description,
      reporter,
      timestamp
    });

    if (!reportId) {
      console.error('❌ No report ID provided');
      navigate('/app');
      return;
    }

    // Build redirect URL with all parameters
    const params = new URLSearchParams({
      sharedReport: reportId,
      ...(reportType && { reportType }),
      ...(location && { location }),
      ...(description && { description }),
      ...(reporter && { reporter }),
      ...(timestamp && { timestamp }),
    });

    const redirectUrl = `/app?${params.toString()}`;
    console.log('✅ Redirecting to:', redirectUrl);
    
    // Redirect to main app with parameters
    navigate(redirectUrl, { replace: true });
  }, [searchParams, navigate]);

  // Show loading state while redirecting
  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-emerald-50 flex items-center justify-center">
      <div className="text-center">
        <div className="w-16 h-16 border-4 border-green-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <h2 className="text-xl font-bold text-gray-900 mb-2">Opening report...</h2>
        <p className="text-sm text-gray-600">Taking you to the news feed</p>
      </div>
    </div>
  );
}